<!-- Copyright 2023, Common Good Learning Tools LLC -->
<template>
	<k-dialog v-model="dialog_open" max-width="800" persistent scrollable>
		<k-card>
			<k-card-title><h3>{{dialog_title}}</h3></k-card-title>
			<k-card-text style="font-size:16px">

				<div v-if="stage=='switch_to_teacher_intro'">
					<div class="mb-3">You are currently registered with a <b>STUDENT</b> account to {{site_config.app_name}}. Click the button below to switch your registration to a <b>TEACHER</b> account.</div>
					<div class="text-center"><k-btn color="primary" @click="stage='switching_to_teacher'"><k-icon small class="mr-2">fas fa-person-chalkboard</k-icon> Switch to Teacher Account</k-btn></div>
				</div>

				<div v-if="stage=='switching_to_teacher'" class="mt-3">
					<div class="mt-3" style="font-size:18px" v-html="switching_to_teacher_intro"></div>
					<div style="width:520px; margin:auto">
						<div class="mt-3 text-center"><b>To register with {{site_config.app_name}} as a teacher, please:</b></div>
						<ul class="mt-2">
							<li>identify your US state or country</li>
							<li>then identify the school district or institution where you teach.</li>
						</ul>
					</div>
					<div class="d-flex mt-5">
						<k-spacer/>
						<div style="width:160px">
							<k-select label="State/Country" outlined hide-details append-icon="fas fa-caret-down"
								v-model="selected_state"
								:items="states"
								@change="state_changed"
							></k-select>
						</div>
						<div v-if="selected_state" style="width:620px" class="ml-3">
							<k-autocomplete ref="district_autocomplete" outlined hide-details label="District/Institution"  append-icon="fas fa-caret-down"
								v-model="selected_district"
								:items="state_districts"
								:search-input.sync="district_input"
								hide-no-data
								@keydown="handle_keydown"
								@blur="handle_blur"
								></k-autocomplete>
						</div>
						<k-spacer/>
					</div>
					<div v-if="selected_district_title" class="text-center mt-3">
						<b>{{selected_district_title}}</b>
					</div>
				</div>

				<div v-if="stage=='switching_to_student'">
					<div class="mb-3">You are currently registered with a <b>TEACHER</b> account to {{site_config.app_name}}. Click the button below to switch your {{site_config.app_name}} registration to a <b>STUDENT</b> account.</div>
					<div class="red--text text--darken-3"><b>NOTE:</b> The primary reason for switching to a student account is that you are a student who accidentally registered with {{site_config.app_name}} as a teacher.</div>
				</div>

			</k-card-text>
			<k-card-actions class="pa-3" style="border-top:1px solid #999">
				<k-btn color="secondary" @click="$emit('dialog_cancel')"><k-icon small class="mr-2">fas fa-xmark</k-icon>Cancel</k-btn>
				<k-spacer></k-spacer>
				<k-btn color="primary" :disabled="save_disabled" @click="save_changes">{{action_btn_text}}<k-icon small class="ml-2">fas fa-arrow-right</k-icon></k-btn>
			</k-card-actions>
		</k-card>
	</k-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	props: {
		// req: { type: String, required: true },
		// nreq: { type: String, required: false, default() { return ''} },
	},
	data() { return {
		dialog_open: true,
		dialog_title: '',
		action_btn_text: '',
		stage: '',
		states: [],
		state_districts: [],
		selected_state: '',
		district_input: '',
		selected_district: '',
		district_autocomplete_input: '',

		role_to_switch_to: 'staff',
	}},
	computed: {
		...mapState(['site_config', 'school_districts', 'user_info']),
		...mapGetters(['studentish_role']),
		save_disabled() {
			if (this.stage == 'switching_to_student') return false
			return empty(this.selected_district_title)
		},
		selected_district_title() {
			if (this.selected_district && this.selected_district != this.district_input) {
				let o = this.school_districts.find(x=>x.agency_id == this.selected_district)
				if (o) {
					return `${o.agency_name} (${o.state})`
				} else {
					return ''
				}
			} else if (!empty(this.district_input)) {
				return `${this.district_input} (${this.selected_state})`
			}
		},
		switching_to_teacher_intro() {
			// this could move to config...
			let s = `${this.site_config.app_name} is an online community provided by Common Good Learning Tools to provide teachers with access to Sparkl, our free teaching and learning tool. <b>Anyone</b> is welcome to learn about Sparkl and browse sample activities here without signing in, and <nobr><b>any teacher</b></nobr> is welcome to create an account to create or copy activities for use with their students.`
			return s
		},
	},
	watch: {
	},
	created() {
		vapp.user_role_selection_component = this

		// use these for testing
		// this.$store.state.create_account_clicked = ''
		// this.$store.state.new_user_created = ''
		// this.$store.state.user_info.role = 'student'

		// establish state
		// Clicked “Create Account” from signin page
		if (this.$store.state.create_account_clicked == 'yes') {
			this.$store.state.create_account_clicked = ''		// we only want this state once
			this.dialog_title = 'Create Teacher Account'
			this.action_btn_text = 'Create Teacher Account'
			this.stage = 'switching_to_teacher'
	
		// Signed in with Google for the first time
		} else if (this.$store.state.new_user_created == 'yes') {
			this.$store.state.new_user_created = ''		// we only want this state once
			this.dialog_title = 'Register for a Teacher Account'
			this.action_btn_text = 'Register For Teacher Account'
			this.stage = 'switching_to_teacher'

		// Previously created student account; clicked “Manage User Role” from user menu
		} else if (this.studentish_role) {
			this.dialog_title = 'Manage User Role'
			this.action_btn_text = 'Register For Teacher Account'
			this.stage = 'switch_to_teacher_intro'

		// Previously created teacher account; clicked “Manage User Role” from user menu
		} else {
			this.dialog_title = 'Manage User Role'
			this.action_btn_text = 'Switch To Student Account'
			this.stage = 'switching_to_student'
		}

		// note that /src/config/school_districts.tsv must exist to load the list of districts
		this.$store.dispatch('load_school_districts').then(x=>{
			// start with states from the district list
			let arr = [...new Set(this.school_districts.map(agency => agency.state))].sort()
			// add some countries at the top
			let countries =
				[
					'Australia',
					'Canada',
					'Great Britain',
					'Mexico',
					'New Zealand',
					'OTHER COUNTRY',
				]
			this.states = [...arr, ...countries]
		})
	},
	mounted() {
	},
	methods: {
		state_changed() {
			// compose state_districts array for use in district drop-down
			this.state_districts = this.school_districts
				.filter(row => row.state === this.selected_state)
				.map(row => ({
					value: row.agency_id,
					text: `${row.agency_name} [${row.county}${
						(row.student_count*1 > 0) ? (': ~' + (Math.round(row.student_count*1)+'').replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' students') : ''
					}]`
				}))
				.sort((a, b) => a.text.localeCompare(b.text))
		},

		handle_blur(event) {
			console.warn('handle_blur')
			this.finish(event)
		},

		// when a keydown happens...
		handle_keydown(event) {
			// if the user hits enter, finish processing
			if (event.keyCode == 13) {
				this.finish(event)
				
			// else clear selected_district, so that selected_district_title will use district_input
			} else {
				// console.log('clear selected_district')
				this.selected_district = ''
			}
		},

		finish(event) {
			// console.warn(`finish ${this.selected_district} / ${this.district_input}`)
			let current_input = this.district_input
			
			// stop Vuetify from seeing the triggering event and running its 'clear' logic
			event.stopImmediatePropagation()
			event.preventDefault()

			if (current_input) {
				// if the user didn't actually select something from the menu, update selected_district to the current value
				if (empty(this.selected_district)) {
					this.selected_district = current_input
				}
				
				// force the search query to stay by setting district_input to current_input
				// we have to wait a tick to do this, because Vuetify may try to clear the value of district_input, due to the fact that we've set selected_district to a value that isn't in state_districts
				setTimeout(() => {
					// console.warn(current_input)
					this.district_input = current_input
					// blur the input to hide the menu (I'm not sure why we don't get an infinite loop here, but it seems to work...)
					event.target.blur()
				}, 0)
			}
		},

		async save_changes() {
			let title, text, acceptText
			let new_role
			if (this.stage == 'switching_to_student') {
				new_role = 'student'
				title = 'Confirm'
				text = `Please confirm that you wish to switch your ${this.site_config.app_name} registration to a student account.`
				acceptText = 'I Am A Student'

			// switching to teacher
			} else {
				new_role = 'staff'
				title = 'Confirm Teacher Status'
				text = `Please confirm that you are a teacher in/at ${this.selected_district_title}.`
				acceptText = 'I Am A Teacher'
			}

			let confirmed = await U.confirm_promise({
				title,
				text,
				acceptText,
				acceptIcon: 'fas fa-check',
				cancelIcon: 'fas fa-xmark',
				dialogMaxWidth: 500,
			})

			// if user clicks cancel, bail out
			if (!confirmed) return

			// user clicked OK, so continue processing
			let payload = {
				user_id: this.user_info.user_id,
				new_role: new_role,
			}
			if (new_role == 'staff') {
				payload.registered_agency = this.selected_district_title
			}

			U.loading_start()
			let ajax_result = await U.ajax_promise('switch_user_role_initiated_by_user', payload)
			U.loading_stop()
			if (ajax_result.status != 'ok') {
				// handle error
				this.$alert('An error occurred when attempting to update your registration.')
				return
			}

			this.$alert('Your registration has been updated.').then(x=>{
				// refresh window to re-initialize with new role
				window.location.reload()
			})
		},
	}
}
</script>

<style lang="scss">
</style>